import type { ReactElement } from 'react';
import type { GuestGender } from '@room-mates/shared';
import { hashString, pickSeeded } from './seededRandom.js';

const OUTFIT_COLORS = [
  '#c1622b',
  '#3f7d3a',
  '#a5321f',
  '#6b4fa0',
  '#1f6f8b',
  '#a06a12',
  '#8a4b6e',
  '#3a6b52',
];
const SKIN_TONES = ['#f2cba0', '#e0a877', '#c67f4e', '#8a5a35', '#6b4226'];
const HAIR_COLORS = ['#2a1a10', '#4a2f1a', '#7a4a20', '#c99a3f', '#4a4a4a', '#8a3d2e'];
const HAT_COLORS = ['#5a3a22', '#3f5a7a', '#7a3f5a', '#3f7a5a', '#7a5a3f', '#3f3f7a'];
const HAIR_STYLES = ['bare', 'short', 'curly', 'bun', 'ponytail'] as const;
const HAT_STYLES = ['pointed-hat', 'headscarf', 'cap', 'beanie'] as const;
const HEADWEAR_STYLES = [...HAIR_STYLES, ...HAT_STYLES] as const;
const EXPRESSIONS = ['smile', 'neutral', 'grin', 'smirk'] as const;
const ACCESSORIES = ['none', 'glasses', 'freckles', 'mustache'] as const;

type HatStyle = (typeof HAT_STYLES)[number];

function isHat(value: (typeof HEADWEAR_STYLES)[number]): value is HatStyle {
  return (HAT_STYLES as readonly string[]).includes(value);
}

const SIZE_PX: Record<'sm' | 'md' | 'lg' | 'xl', number> = { sm: 28, md: 40, lg: 56, xl: 80 };

export interface GuestAvatarProps {
  readonly guestId: string;
  readonly name: string;
  readonly gender: GuestGender;
  /** Bumped to reshuffle the look on demand, e.g. from a "redraw" button. */
  readonly nonce?: number;
  readonly size?: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * A little character for a guest — always the same one for the same
 * `guestId`, `gender` and `nonce`, picked deterministically rather than with
 * `Math.random()`, so it doesn't reshuffle on every render or every visit.
 * Outfit colour, skin tone, headwear (five haircuts or four hats), hair
 * colour, hat colour, facial expression and one accessory are all picked
 * independently, so two guests rarely end up looking alike. `gender` is
 * folded into the seed rather than steering any single trait, so the look
 * genuinely changes when it changes without the component presuming what a
 * given gender "should" look like.
 */
export function GuestAvatar({
  guestId,
  name,
  gender,
  nonce = 0,
  size = 'md',
}: GuestAvatarProps): ReactElement {
  const seed = `${guestId}:${gender}:${nonce}`;
  const outfit = pickSeeded(hashString(`${seed}:outfit`), OUTFIT_COLORS);
  const skin = pickSeeded(hashString(`${seed}:skin`), SKIN_TONES);
  const headwear = pickSeeded(hashString(`${seed}:headwear`), HEADWEAR_STYLES);
  const hairColor = pickSeeded(hashString(`${seed}:haircolor`), HAIR_COLORS);
  const hat = pickSeeded(hashString(`${seed}:hat`), HAT_COLORS);
  const expression = pickSeeded(hashString(`${seed}:expression`), EXPRESSIONS);
  const accessory = pickSeeded(hashString(`${seed}:accessory`), ACCESSORIES);
  const dimension = SIZE_PX[size];
  const headwearColor = isHat(headwear) ? hat : hairColor;

  return (
    <span className={`guest-avatar guest-avatar--${size}`} aria-hidden="true" title={name}>
      <svg
        key={`${headwear}-${headwearColor}-${expression}-${accessory}-${outfit}`}
        className="pop-in"
        viewBox="0 0 64 64"
        width={dimension}
        height={dimension}
      >
        <circle cx="32" cy="32" r="32" fill="var(--surface-sunken)" />

        {/* t-shirt: sleeves first so the torso overlaps their inner edge */}
        <circle cx="16" cy="47" r="6.5" fill={outfit} />
        <circle cx="48" cy="47" r="6.5" fill={outfit} />
        <path d="M14 62 Q14 40 32 40 Q50 40 50 62 Z" fill={outfit} />
        <path
          d="M25 41 Q32 48 39 41"
          stroke="rgba(0, 0, 0, 0.15)"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />

        {headwear === 'ponytail' ? (
          <path d="M46 20 Q56 24 52 38 Q47 30 44 22 Z" fill={hairColor} />
        ) : null}

        <circle cx="32" cy="26" r="15" fill={skin} />

        <Face expression={expression} />

        {headwear === 'short' ? (
          <path d="M16 22 Q32 4 48 22 Q32 14 16 22 Z" fill={hairColor} />
        ) : null}
        {headwear === 'curly' ? (
          <>
            <circle cx="18" cy="18" r="6" fill={hairColor} />
            <circle cx="26" cy="12" r="6" fill={hairColor} />
            <circle cx="32" cy="10" r="6" fill={hairColor} />
            <circle cx="38" cy="12" r="6" fill={hairColor} />
            <circle cx="46" cy="18" r="6" fill={hairColor} />
          </>
        ) : null}
        {headwear === 'bun' ? (
          <>
            <path d="M17 22 Q32 8 47 22 Q32 15 17 22 Z" fill={hairColor} />
            <circle cx="32" cy="8" r="6" fill={hairColor} />
          </>
        ) : null}
        {headwear === 'ponytail' ? (
          <path d="M16 22 Q32 4 48 22 Q32 14 16 22 Z" fill={hairColor} />
        ) : null}
        {headwear === 'pointed-hat' ? (
          <path d="M14 20 L32 2 L50 20 Q32 12 14 20 Z" fill={hat} />
        ) : null}
        {headwear === 'headscarf' ? (
          <path d="M14 22 Q32 5 50 22 L50 27 Q32 16 14 27 Z" fill={hat} />
        ) : null}
        {headwear === 'cap' ? <path d="M14 18 Q32 6 50 18 L50 22 L14 22 Z" fill={hat} /> : null}
        {headwear === 'beanie' ? (
          <>
            <path d="M15 21 Q32 5 49 21 L49 25 Q32 20 15 25 Z" fill={hat} />
            <circle cx="32" cy="6" r="3.5" fill={hat} />
          </>
        ) : null}

        {accessory === 'glasses' ? (
          <rect
            x="20"
            y="24"
            width="24"
            height="8"
            rx="4"
            fill="none"
            stroke="#3a2c1a"
            strokeWidth="2"
          />
        ) : null}
        {accessory === 'freckles' ? (
          <>
            <circle cx="26" cy="31" r="1.2" fill="#8a5a35" />
            <circle cx="30" cy="33" r="1.2" fill="#8a5a35" />
            <circle cx="36" cy="31" r="1.2" fill="#8a5a35" />
          </>
        ) : null}
        {accessory === 'mustache' ? (
          <path d="M24 32 Q32 36 40 32 Q32 34.5 24 32 Z" fill="#3a2c1a" />
        ) : null}
      </svg>
    </span>
  );
}

/** Eyes and a mouth — the one thing missing that made every face look blank. */
function Face({ expression }: { expression: (typeof EXPRESSIONS)[number] }): ReactElement {
  return (
    <>
      {expression === 'grin' ? (
        <>
          <path d="M13 26 Q16 23 19 26" stroke="#2a2a2a" strokeWidth="1.3" fill="none" />
          <path d="M25 26 Q28 23 31 26" stroke="#2a2a2a" strokeWidth="1.3" fill="none" />
        </>
      ) : (
        <>
          <circle cx="16" cy="26" r="1.3" fill="#2a2a2a" />
          <circle cx="28" cy="26" r="1.3" fill="#2a2a2a" />
        </>
      )}

      {expression === 'smile' ? (
        <path
          d="M14 32 Q22 37 30 32"
          stroke="#2a2a2a"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        />
      ) : null}
      {expression === 'neutral' ? (
        <line
          x1="16"
          y1="33"
          x2="28"
          y2="33"
          stroke="#2a2a2a"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      ) : null}
      {expression === 'grin' ? (
        <path d="M13 31 Q22 39 31 31 Q22 35 13 31 Z" fill="#2a2a2a" opacity="0.85" />
      ) : null}
      {expression === 'smirk' ? (
        <path
          d="M15 32 Q23 35 29 30"
          stroke="#2a2a2a"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        />
      ) : null}
    </>
  );
}
